import { useIntl } from '@leagueplatform/locales';
import {
  SettingsLink,
  UserSettingsLinksConfig,
} from '@leagueplatform/solutions-foundation';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { QUERY_PARAM_MODAL_VALUE } from 'common/constants';
import { isPatientPortalDeveloperMode } from 'common/env';
import { usePatientPortalContext } from 'contexts/patient-portal-provider.context';
import { useExternalLinks } from 'hooks/use-external-links/use-external-links.hook';
import { useUrlParam } from 'hooks/use-url-param/use-url-param.hook';
import { PATIENT_PORTAL_SYSTEM_IDS } from 'types/patient-portal-system.types';

const openInNewTab = (url: string) =>
  window.open(url, '_blank', 'noopener,noreferrer');

export const useUserSettingsLinksConfig = () => {
  const { formatMessage } = useIntl();
  const { setParam } = useUrlParam('modal');
  const { systemId, setSystemId } = usePatientPortalContext();
  const externalLinks = useExternalLinks();

  const isSystemIdAmplar = systemId === PATIENT_PORTAL_SYSTEM_IDS.AMPLAR;

  const userSettingsLinks: UserSettingsLinksConfig[] = [
    {
      heading: formatMessage({ id: 'STR_ACCOUNT' }),
      items: [
        {
          text: formatMessage({ id: 'STR_PERSONAL_INFORMATION' }),
          to: APP_MANAGED_ROUTES.PERSONAL_INFORMATION,
          color: '$onSurfaceTextPrimary',
        },
        ...(!isSystemIdAmplar
          ? ([
              {
                text: formatMessage({ id: 'STR_YOUR_CENTRES' }),
                to: APP_MANAGED_ROUTES.LINKED_CENTRES,
                color: '$onSurfaceTextPrimary',
              },
            ] as SettingsLink[])
          : []),
      ],
    },
    {
      heading: formatMessage({ id: 'STR_GET_SUPPORT' }),
      items: [
        ...(externalLinks.FAQ
          ? ([
              {
                text: formatMessage({ id: 'STR_FAQ' }),
                onClick: () => openInNewTab(externalLinks.FAQ),
                color: '$onSurfaceTextPrimary',
              },
            ] as SettingsLink[])
          : []),
      ],
    },
    {
      heading: formatMessage({ id: 'STR_ABOUT' }),
      items: [
        {
          text: formatMessage({ id: 'STR_CONTACT_US' }),
          to: APP_MANAGED_ROUTES.CONTACT_US,
          color: '$onSurfaceTextPrimary',
        },
        ...(externalLinks.PRIVACY
          ? ([
              {
                text: formatMessage({ id: 'STR_COLLECTION_NOTICE' }),
                onClick: () => openInNewTab(externalLinks.PRIVACY),
                color: '$onSurfaceTextPrimary',
              },
            ] as SettingsLink[])
          : []),
        ...(externalLinks.TERMS
          ? ([
              {
                text: formatMessage({ id: 'STR_TOS' }),
                onClick: () => openInNewTab(externalLinks.TERMS),
                color: '$onSurfaceTextPrimary',
              },
            ] as SettingsLink[])
          : []),
        {
          text: formatMessage({ id: 'STR_ACCOUNT_DELETE' }),
          onClick: () => {
            setParam(QUERY_PARAM_MODAL_VALUE.DELETE_ACCOUNT);
          },
          color: '$onSurfaceTextPrimary',
        },
      ],
    },
    ...(isPatientPortalDeveloperMode()
      ? ([
          {
            heading: formatMessage({ id: 'STR_DEVELOPER' }),
            items: [
              {
                text: formatMessage({ id: 'STR_SYSTEM_ID' }, { id: systemId }),
                onClick: () => {
                  setSystemId(
                    isSystemIdAmplar
                      ? PATIENT_PORTAL_SYSTEM_IDS.MY_HEALTH
                      : PATIENT_PORTAL_SYSTEM_IDS.AMPLAR,
                  );
                },
                color: '$onSurfaceTextPrimary',
              },
            ],
          },
        ] as UserSettingsLinksConfig[])
      : []),
    {
      items: [
        {
          text: formatMessage({ id: 'STR_SIGN_OUT' }),
          to: APP_MANAGED_ROUTES.SIGN_OUT,
          color: '$onSurfaceTextPrimary',
        },
      ],
    },
  ];

  return { userSettingsLinks };
};
